/**
 * buildRenderContainer — lightweight rendering-only AtomContainer (PIPH-68).
 *
 * Drop-in replacement for `toAtomContainer(workspace)` inside `useSVGGeneration`,
 * dramatically cheaper because:
 *
 *   1. No class instances. Plain object literals for atoms and bonds — skips
 *      `new AtomModel()` and `new BondModel()` constructor overhead and the
 *      `ElementHelper.getAtomicNumberBySymbol` lookup that AtomModel does in
 *      its constructor (~440 lookups per render at 20 templates).
 *
 *   2. O(1) `getBondsAttachedTo`. AtomContainer's implementation filters all
 *      bonds on every call — O(n) per call, O(n²) total for the per-atom
 *      iteration in `svgPaperAtoms`. This builds an `atomId → bonds[]` map
 *      once and looks up in O(1).
 *
 *   3. Y-flips positions inline. The legacy path mutates the AtomContainer's
 *      atom positions to flip y from chemistry (y-up) to SVG (y-down). This
 *      builds positions y-flipped from the start, so the downstream pipeline
 *      doesn't need a mutation pass.
 *
 *   4. Preserves the AtomContainer shape exactly. The downstream rendering
 *      functions (svgPaperAtoms, atomRecipes, bondRecipes, svgElectrons,
 *      svgCharge, svgArrows, Rings) consume the returned object unchanged.
 *      A future PIPH-68 follow-up can refactor those to consume MolecularWorkspace
 *      directly and remove this shim entirely.
 *
 * The container returned is **read-only** for the rendering pipeline. Mutation
 * methods (`addAtom`, `addBond`, `clear`, `combine`, `split`, etc.) are not
 * implemented because rendering never mutates. If a future caller needs them,
 * they would have to use `toAtomContainer` instead.
 *
 * @package @alchemiesolns/svg-interactive
 */

import { type MolecularWorkspace, makeCompoundId } from '@alchemiesolns/molecular-workspace';
import {
  type AtomContainer,
  type IAtomModel,
  type IBondModel,
} from '@alchemiesolns/atomcontainer';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';

/**
 * Build a lightweight, render-only AtomContainer from a MolecularWorkspace.
 *
 * The returned container is structurally compatible with AtomContainer for
 * the purposes of svg-interactive's rendering pipeline, but is much cheaper
 * to construct than `toAtomContainer`.
 *
 * Mutation operations are not supported — the container is intended to be
 * thrown away at the end of each render.
 */
export function buildRenderContainer(workspace: MolecularWorkspace): AtomContainer {
  // Stub method shared by all atoms — never called by rendering code,
  // but required by IAtomModel's interface contract.
  const stubCopy = function (this: IAtomModel): IAtomModel { return this; };

  // ----- Pass 1: build atom shells with y-flipped positions ----------------
  const atomById = new Map<string, IAtomModel>();
  const atoms: IAtomModel[] = [];

  for (const wsAtom of workspace.getAllLooseAtoms()) {
    const atom: IAtomModel = {
      id: wsAtom.id,
      symbol: wsAtom.symbol,
      charge: wsAtom.charge,
      lonePairs: wsAtom.lonePairs,
      radicals: wsAtom.radicals,
      // Y-FLIP: workspace is y-up (chemistry), SVG is y-down. Flip once here
      // so the downstream pipeline doesn't have to mutate.
      pos: { x: wsAtom.x, y: -wsAtom.y, z: 0 },
      visible: workspace.getVisibility(wsAtom.id) !== VisibilityFlag.Hidden,
      // Fields required by IAtomModel but unused by the rendering pipeline.
      // We deliberately skip ElementHelper.getAtomicNumberBySymbol — saves
      // a hash lookup per atom on every render.
      atomicNumber: 0,
      valence: 0,
      isAliphatic: false,
      isAromatic: false,
      copy: stubCopy,
    };
    atoms.push(atom);
    atomById.set(wsAtom.id, atom);
  }

  // ----- Pass 1b: add compound-ID atoms from collapsed instances -------------
  // This allows connective bonds (between instances and loose atoms) to flow
  // through the normal bond rendering pipeline (BondLayer/DynamicBond).
  for (const instance of workspace.getGroupInstances()) {
    const template = workspace.getTemplate(instance.templateId);
    if (!template) continue;
    const { tx, ty, rotation } = instance.transform;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    for (const localAtom of template.workspace.getAllAtoms()) {
      const compoundId = makeCompoundId(instance.id, localAtom.id);
      if (atomById.has(compoundId)) continue; // Already a loose atom (expanded)
      const worldX = localAtom.x * cos - localAtom.y * sin + tx;
      const worldY = localAtom.x * sin + localAtom.y * cos + ty;
      const vis = template.workspace.getVisibility(localAtom.id);
      const atom: IAtomModel = {
        id: compoundId,
        symbol: localAtom.symbol,
        charge: localAtom.charge,
        lonePairs: localAtom.lonePairs,
        radicals: localAtom.radicals,
        pos: { x: worldX, y: -worldY, z: 0 },
        visible: vis !== VisibilityFlag.Hidden,
        atomicNumber: 0,
        valence: 0,
        isAliphatic: false,
        isAromatic: false,
        copy: stubCopy,
      };
      // Don't push to atoms[] — we don't want instance atoms rendered by AtomLayer.
      // Only add to atomById so connective bonds can resolve their endpoints.
      atomById.set(compoundId, atom);
    }
  }

  // Track which atom IDs are loose (in atoms[]) vs shadow (compound-ID only in atomById)
  const looseAtomIds = new Set(atoms.map(a => a.id!));

  // ----- Pass 2: build bonds + adjacency map for O(1) lookup ----------------
  // The adjacency map is the big win: AtomContainer.getBondsAttachedTo filters
  // all bonds on every call (O(n)). With ~440 atoms × 460 bonds in a 20-template
  // workspace, the per-render cost was ~200K operations just for adjacency.
  const bondsByAtomId = new Map<string, IBondModel[]>();
  const bonds: IBondModel[] = [];
  // Connective bonds (at least one endpoint is a compound-ID instance atom).
  // Kept out of bonds[] and bondsByAtomId to avoid crashing ring detection,
  // which expects all bond endpoints to be in atoms[].
  const connectiveBonds: IBondModel[] = [];

  // Pre-allocate adjacency lists for loose atoms only (ring detection walks these).
  for (const id of looseAtomIds) {
    bondsByAtomId.set(id, []);
  }

  for (const wsBond of workspace.getAllLooseBonds()) {
    const a1 = atomById.get(wsBond.a1.id);
    const a2 = atomById.get(wsBond.a2.id);
    if (!a1 || !a2) continue; // Skip bonds with unresolvable endpoints

    const bond: IBondModel = {
      id: wsBond.id,
      a1,
      a2,
      order: wsBond.order,
      type: wsBond.bondType,
      isAromatic: false,
      isAliphatic: false,
      // Methods required by IBondModel. `reverseBond` must match BondModel:
      // svgElectrons.calcElectronPosition calls it so the current atom is `a1`
      // before AtomContainerQuery.getBondDirection (a1 → a2) runs; a no-op
      // breaks lone-pair placement (bond vectors point the wrong way).
      getOtherAtom(a) { return a === this.a1 ? this.a2 : this.a1; },
      isAttachedTo(a) { return a === this.a1 || a === this.a2; },
      reverseBond() {
        const t = this.a1;
        this.a1 = this.a2;
        this.a2 = t;
        this.flipChirality();
      },
      flipChirality() {
        if (!this.type) return;
        if (this.type === '\\') {
          this.type = '/';
        } else if (this.type === '/') {
          this.type = '\\';
        } else if (this.type === 'w') {
          this.type = 'd';
        } else if (this.type === 'd') {
          this.type = 'w';
        }
      },
      copy() { return this; },
    };

    const a1Loose = looseAtomIds.has(wsBond.a1.id);
    const a2Loose = looseAtomIds.has(wsBond.a2.id);
    if (a1Loose && a2Loose) {
      bonds.push(bond);
      bondsByAtomId.get(wsBond.a1.id)!.push(bond);
      bondsByAtomId.get(wsBond.a2.id)!.push(bond);
    } else {
      connectiveBonds.push(bond);
    }
  }

  // ----- Build container shell with O(1) getBondsAttachedTo ----------------
  // Use a plain object with the AtomContainer surface that rendering touches.
  // The unused mutation methods stay undefined; if anything calls them at
  // runtime, it'll throw a clear "is not a function" error rather than
  // silently doing the wrong thing.
  const container = {
    atoms,
    bonds,
    connectiveBonds,
    getBondsAttachedTo(atom: IAtomModel): IBondModel[] {
      return bondsByAtomId.get(atom.id ?? '') ?? [];
    },
    getAtomById(id: string): IAtomModel | undefined {
      return atomById.get(id);
    },
    getBondById(id: string): IBondModel | undefined {
      return bonds.find((b) => b.id === id) ?? connectiveBonds.find((b) => b.id === id);
    },
  } as unknown as AtomContainer;

  return container;
}
